import type { IntlShape } from 'react-intl';
import { yellow, green, blue } from '@mui/material/colors';
import type { ShiftResource } from '~/generated';
import type { Turn } from '~/hooks/api/useShift';
import {
  CHANGED,
  COMMITTED,
  FINALIZED,
} from '~/components/ShiftDetailCardAssignmentStatus/constants';
import assignmentStatusMessages from '~/components/ShiftDetailCardAssignmentStatus/messages';
import messages from './messages';

export type ShiftWithTurns = ShiftResource & {
  id: number;
  detailedTurns?: Turn[];
};

export type FilteredShift = {
  shift: ShiftWithTurns;
  turns: Turn[];
};

export const filterShifts = (shifts: ShiftWithTurns[]): FilteredShift[] =>
  shifts
    .map((shift) => ({
      shift,
      turns: (shift.detailedTurns ?? []).filter(
        (turn) =>
          turn.isEnabled &&
          !turn.isCanceled &&
          turn.turnAssignments &&
          turn.turnAssignments.length > 0
      ),
    }))
    .filter(({ turns }) => turns.length > 0);

export const filterShiftsAppliedByPublisher = (
  shifts: ShiftWithTurns[],
  publisherId: number
): FilteredShift[] =>
  shifts
    .filter((shift) =>
      (shift.applications ?? []).some(
        (a) => (a as ApplicationShape).publisher?.id === publisherId
      )
    )
    .map((shift) => ({
      shift,
      turns: (shift.detailedTurns ?? []).filter(
        (turn) => turn.isEnabled && !turn.isCanceled
      ),
    }));

export const getPublisherDisplayName = (record: {
  firstName: string;
  lastName: string;
}): string => {
  return `${record.firstName} ${record.lastName}`;
};

export type PublisherAssignmentCount = {
  publisherId: number;
  firstName: string;
  lastName: string;
  count: number;
};

export type UnassignedApplicant = {
  publisherId: number;
  firstName: string;
  lastName: string;
  appliedShiftIds: number[];
};

type ApplicationShape = {
  publisher?: {
    id?: number | null;
    firstName?: string;
    lastName?: string;
  } | null;
  // Reference fields are either the expanded resource or the raw id (number)
  // when the caller did not request expansion of that field.
  plannedTurn?: number | { id?: number | null } | null;
};

export const getUnassignedApplicants = (
  shifts: Array<{ id?: number | null; applications?: unknown[] | null }>
): UnassignedApplicant[] => {
  const candidates = new Map<number, UnassignedApplicant>();
  const assignedPublisherIds = new Set<number>();

  for (const shift of shifts) {
    if (shift.id == null) continue;
    for (const raw of shift.applications ?? []) {
      const application = raw as ApplicationShape;
      const publisher = application.publisher;
      if (!publisher || publisher.id == null) continue;

      if (application.plannedTurn != null) {
        assignedPublisherIds.add(publisher.id);
        continue;
      }

      const existing = candidates.get(publisher.id);
      if (existing) {
        existing.appliedShiftIds.push(shift.id);
      } else {
        candidates.set(publisher.id, {
          publisherId: publisher.id,
          firstName: publisher.firstName ?? '',
          lastName: publisher.lastName ?? '',
          appliedShiftIds: [shift.id],
        });
      }
    }
  }

  for (const id of assignedPublisherIds) {
    candidates.delete(id);
  }

  return [...candidates.values()].sort((a, b) => {
    const lastCmp = a.lastName.localeCompare(b.lastName);
    if (lastCmp !== 0) return lastCmp;
    return a.firstName.localeCompare(b.firstName);
  });
};

export const getPublishersWithMultipleAssignments = (
  filteredShiftsByDay: Map<number, FilteredShift[]>
): PublisherAssignmentCount[] => {
  const counts = new Map<number, PublisherAssignmentCount>();

  for (const shifts of filteredShiftsByDay.values()) {
    for (const { turns } of shifts) {
      for (const turn of turns) {
        for (const assignment of turn.turnAssignments) {
          const record = assignment.publisherMiniRecord;
          if (!record) continue;
          const existing = counts.get(record.id);
          if (existing) {
            existing.count++;
          } else {
            counts.set(record.id, {
              publisherId: record.id,
              firstName: record.firstName,
              lastName: record.lastName,
              count: 1,
            });
          }
        }
      }
    }
  }

  return [...counts.values()]
    .filter((p) => p.count > 1)
    .sort((a, b) => b.count - a.count);
};

export const getColorForAssignmentState = (state?: string | null) => {
  switch (state) {
    case CHANGED:
      return yellow[800];
    case COMMITTED:
      return blue[800];
    case FINALIZED:
      return green[800];
    default:
      return yellow[800];
  }
};

export const getAssignmentStateLabel = (
  intl: IntlShape,
  state?: string | null
) => {
  switch (state) {
    case COMMITTED:
      return intl.formatMessage(messages.assignmentStateCommitted);
    case FINALIZED:
      return intl.formatMessage(messages.assignmentStateFinalized);
    default:
      return intl.formatMessage(messages.assignmentStateChanged);
  }
};

export const getAssignmentStateDescription = (
  intl: IntlShape,
  state?: string | null
) => {
  switch (state) {
    case COMMITTED:
      return intl.formatMessage(assignmentStatusMessages.committedDescription);
    case FINALIZED:
      return intl.formatMessage(assignmentStatusMessages.finalizedDescription);
    default:
      return intl.formatMessage(assignmentStatusMessages.changedDescription);
  }
};
